import React, { useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useProvider } from '../lib/provider';
import { useAuth } from '../hooks/useAuth';
import { CheckCircle, Circle, Trophy, Star, History, Clock, Check, Gift } from 'lucide-react';
import { isToday } from '../lib/utils';
import { useTranslation } from '../hooks/useTranslation';
import { NestQAvatar } from '../components/NestQAvatar';

export const KidHome: React.FC = () => {
    const provider = useProvider();
    const { session } = useAuth();
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    const locale = session.user?.language === 'sl' ? 'sl-SI' : 'en-US';

    // Real-time subscription for quest completion updates
    useEffect(() => {
        if (!session.user?.id || !provider.getSupabaseClient) return;

        const supabase = provider.getSupabaseClient();
        
        // Subscribe to quest completions for this kid
        const subscription = supabase
            .channel('quest_completions_kid')
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'quest_completions',
                    filter: `kid_user_id=eq.${session.user.id}`
                },
                (payload: any) => {
                    console.log('Quest completion update detected for kid:', payload);
                    
                    // Refresh relevant queries when completions change
                    queryClient.invalidateQueries({ queryKey: ['kid-quests', session.user!.id] });
                    queryClient.invalidateQueries({ queryKey: ['kid-completions', session.user!.id] });
                    queryClient.invalidateQueries({ queryKey: ['kid-history', session.user!.id] });
                    queryClient.invalidateQueries({ queryKey: ['balance', session.user!.id] });
                }
            )
            .subscribe();

        return () => {
            subscription.unsubscribe();
        };
    }, [session.user?.id, provider, queryClient]);

    // Determine greeting based on time of day
    const getTimeBasedGreeting = useMemo(() => {
        const now = new Date();
        const hour = now.getHours();
        const firstName = session.user?.display_name || 'Hero';
        
        if (hour >= 6 && hour < 8) {
            // Early morning (6:00 - 7:59)
            const greeting = t?.kidHome?.earlyMorningGreeting || 'Dobro jutro';
            return `${greeting}, ${firstName}`;
        } else if (hour >= 8 && hour < 9) {
            // Running time (8:00 - 8:59)
            const greeting = t?.kidHome?.runningGreeting || "Let's Run";
            return `${greeting}, ${firstName}`;
        } else if (hour >= 9 && hour < 18) {
            // Daytime (9:00 - 17:59)
            const greeting = t?.kidHome?.hello || 'Živjo';
            return `${greeting}, ${firstName}`;
        } else if (hour >= 18 && hour < 19) {
            // Teeth brushing time (18:00 - 18:59)
            const greeting = t?.kidHome?.teethGreeting || 'Rghmmmm';
            return `${greeting}, ${firstName}`;
        } else {
            // Nighttime/Bedtime (19:00 - 5:59)
            const greeting = t?.kidHome?.nightGreeting || 'Zzzzzz';
            return `${greeting}, ${firstName}`;
        }
    }, [session.user?.display_name, t]);

    const { data: balance, isLoading: loadingBalance } = useQuery({
        queryKey: ['balance', session.user?.id],
        queryFn: () => provider.getBalance(session.user!.id),
        enabled: !!session.user?.id,
    });

    const { data: assignedQuests, isLoading: loadingQuests } = useQuery({
        queryKey: ['kid-quests', session.user?.id],
        queryFn: () => provider.getKidQuests(session.user!.id),
        enabled: !!session.user?.id,
    });

    const { data: completions, isLoading: loadingCompletions } = useQuery({
        queryKey: ['kid-completions', session.user?.id],
        queryFn: () => provider.getKidCompletions(session.user!.id),
        enabled: !!session.user?.id,
    });

    const { data: history, isLoading: loadingHistory } = useQuery({
        queryKey: ['kid-history', session.user?.id],
        queryFn: () => provider.getKidHistory(session.user!.id),
        enabled: !!session.user?.id,
    });

    const completeQuest = useMutation({
        mutationFn: (questId: string) => provider.completeQuest({
            quest_id: questId,
            kid_user_id: session.user!.id,
        }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['kid-quests'] });
            queryClient.invalidateQueries({ queryKey: ['kid-completions'] });
        },
    });

    // Helper functions (must be defined before useMemo that uses them)
    const getFrequencyLabel = (freq: string) => {
        if (freq === 'daily') return t?.parentQuests?.daily || 'Daily';
        if (freq === 'weekly') return t?.parentQuests?.weekly || 'Weekly';
        return t?.parentQuests?.oneTime || 'One-time';
    };

    // Check if quest is within its allowed time window
    const isWithinDailyWindow = (quest: any) => {
        if (quest.frequency !== 'daily') return true;
        if (typeof quest.daily_start_hour !== 'number' || typeof quest.daily_end_hour !== 'number') return true;
        const now = new Date();
        const hour = now.getHours();
        return hour >= quest.daily_start_hour && hour < quest.daily_end_hour;
    };

    // Only disable the button if it's a tomorrow quest (marked as duplicate) or outside time window
    const isQuestDisabled = (quest: any & { isDuplicate?: boolean }) => {
        // Disable tomorrow quests - kids can't complete them today
        if (quest.isDuplicate) return true;
        
        // Disable if mutation is pending
        if (completeQuest.isPending) return true;
        
        // Disable today's quests if outside their time window
        if (!quest.isDuplicate && !isWithinDailyWindow(quest)) return true;
        
        return false;
    };

    const getDailyTimeLabel = (quest: any) => {
        if (quest.frequency !== 'daily') return null;
        if (typeof quest.daily_start_hour !== 'number' || typeof quest.daily_end_hour !== 'number') return null;
        return `${quest.daily_start_hour}:00-${quest.daily_end_hour}:00`;
    };

    const getRelativeDayLabel = (date: Date) => {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        if (d.getTime() === today.getTime()) return t?.common?.today || 'Today';
        if (d.getTime() === tomorrow.getTime()) return t?.common?.tomorrow || 'Tomorrow';

        return d.toLocaleDateString(locale, { weekday: 'short', month: 'short', day: 'numeric' });
    };

    const getQuestDayLabel = (quest: any & { isDuplicate?: boolean }) => {
        try {
            // If this is marked as duplicate, show as tomorrow
            if (quest.isDuplicate) {
                return t?.common?.tomorrow || 'Tomorrow';
            }
            
            if (quest.frequency === 'daily') {
                if (typeof quest.daily_end_hour === 'number') {
                    const now = new Date();
                    const hour = now.getHours();
                    if (hour >= quest.daily_end_hour) return t?.common?.tomorrow || 'Tomorrow';
                }
                return t?.common?.today || 'Today';
            }

            if (quest.frequency === 'weekly' && typeof quest.weekly_day === 'number') {
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                const currentDay = today.getDay();
                const diff = (quest.weekly_day - currentDay + 7) % 7;

                if (diff === 0) return t?.common?.today || 'Today';
                if (diff === 1) return t?.common?.tomorrow || 'Tomorrow';

                const next = new Date(today);
                next.setDate(next.getDate() + diff);
                return next.toLocaleDateString(locale, { weekday: 'short' });
            }

            if (quest.frequency === 'one_time') {
                if (quest.due_date) {
                    return getRelativeDayLabel(new Date(quest.due_date));
                }

                if (quest.assignment?.created_at) {
                    return getRelativeDayLabel(new Date(quest.assignment.created_at));
                }
            }

            return null;
        } catch (error) {
            console.error('Error getting quest day label:', error);
            return null;
        }
    };

    // Memoized computed values (all hooks must be before any conditional returns)
    const activeQuests = React.useMemo(() => {
        if (!assignedQuests) return [];
        
        // Filter out completed one-time quests
        const approvedOneTimeQuestIds = new Set(
            completions
                ?.filter(c => c.status === 'approved' && c.quest.frequency === 'one_time')
                .map(c => c.quest_id) || []
        );
        
        // Get today's quests (not completed today)
        const getTodaysQuests = () => {
            const activeDailyQuests = assignedQuests.filter(q => {
                if (q.frequency !== 'daily') return false;
                if (approvedOneTimeQuestIds.has(q.id)) return false;
                
                const completedToday = completions?.some(c => 
                    c.quest_id === q.id && 
                    (c.status === 'approved' || c.status === 'pending') && 
                    isToday(c.completed_at)
                );
                
                // Include if not completed today
                return !completedToday;
            });

            const activeWeeklyQuests = assignedQuests.filter(q => {
                if (q.frequency !== 'weekly') return false;
                if (approvedOneTimeQuestIds.has(q.id)) return false;
                if (typeof q.weekly_day !== 'number') return false;
                if (new Date().getDay() !== q.weekly_day) return false;

                const completedToday = completions?.some(c =>
                    c.quest_id === q.id &&
                    (c.status === 'approved' || c.status === 'pending') &&
                    isToday(c.completed_at)
                );
                return !completedToday;
            });

            const activeOneTimeQuests = assignedQuests.filter(q => {
                if (q.frequency !== 'one_time') return false;
                if (approvedOneTimeQuestIds.has(q.id)) return false;
                if (!q.due_date) return false;
                return isToday(q.due_date);
            });
            
            // Sort by relevance: first by whether it's currently time, then by start hour
            const now = new Date();
            const currentHour = now.getHours();
            
            activeDailyQuests.sort((a, b) => {
                const aStart = typeof a.daily_start_hour === 'number' ? a.daily_start_hour : 0;
                const bStart = typeof b.daily_start_hour === 'number' ? b.daily_start_hour : 0;
                const aEnd = typeof a.daily_end_hour === 'number' ? a.daily_end_hour : 24;
                const bEnd = typeof b.daily_end_hour === 'number' ? b.daily_end_hour : 24;
                
                // Priority 1: Quests that are currently active (current hour between start and end)
                const aIsActive = currentHour >= aStart && currentHour <= aEnd;
                const bIsActive = currentHour >= bStart && currentHour <= bEnd;
                if (aIsActive && !bIsActive) return -1;
                if (!aIsActive && bIsActive) return 1;
                
                // Priority 2: Quests that haven't started yet but are coming up soon
                const aIsUpcoming = currentHour < aStart;
                const bIsUpcoming = currentHour < bStart;
                if (aIsUpcoming && !bIsUpcoming) return -1;
                if (!aIsUpcoming && bIsUpcoming) return 1;
                
                // Priority 3: For quests with same status, sort by start hour
                return aStart - bStart;
            });

            activeWeeklyQuests.sort((a, b) => b.points - a.points);
            activeOneTimeQuests.sort((a, b) => b.points - a.points);

            return [...activeDailyQuests, ...activeWeeklyQuests, ...activeOneTimeQuests];
        };
        
        // Get tomorrow's quests (all daily quests for tomorrow)
        const getTomorrowsQuests = () => {
            const tomorrowDailyQuests = assignedQuests.filter(q => {
                if (q.frequency !== 'daily') return false;
                if (approvedOneTimeQuestIds.has(q.id)) return false;
                
                // Include tomorrow's quests (even if completed today, they'll show for tomorrow)
                return true;
            });
            
            // Sort by start hour
            tomorrowDailyQuests.sort((a, b) => {
                const aStart = typeof a.daily_start_hour === 'number' ? a.daily_start_hour : 0;
                const bStart = typeof b.daily_start_hour === 'number' ? b.daily_start_hour : 0;
                return aStart - bStart;
            });
            
            return tomorrowDailyQuests;
        };
        
        const todaysQuests = getTodaysQuests();
        const tomorrowsQuests = getTomorrowsQuests();
        
        // Combine quests: always show 5 total, prioritizing today's quests
        const finalQuests: any[] = [];
        const targetCount = 5;
        
        // Add today's quests first
        finalQuests.push(...todaysQuests.map(quest => ({
            ...quest,
            isDuplicate: false
        })));
        
        // If we need more quests, add tomorrow's quests
        if (finalQuests.length < targetCount) {
            const remainingNeeded = targetCount - finalQuests.length;
            const tomorrowsToAdd = tomorrowsQuests
                .filter(quest => !finalQuests.some(fq => fq.id === quest.id))
                .slice(0, remainingNeeded);
            
            finalQuests.push(...tomorrowsToAdd.map(quest => ({
                ...quest,
                isDuplicate: true
            })));
        }
        
        return finalQuests.slice(0, targetCount);
    }, [assignedQuests, completions]);

    // Sort active quests: daily quests by start hour, then weekly, then one-time by points
    const sortedActiveQuests = React.useMemo(() => {
        return activeQuests?.sort((a, b) => {
            // Daily quests first, sorted by start hour
            if (a.frequency === 'daily' && b.frequency === 'daily') {
                const aStart = typeof a.daily_start_hour === 'number' ? a.daily_start_hour : 0;
                const bStart = typeof b.daily_start_hour === 'number' ? b.daily_start_hour : 0;
                return aStart - bStart;
            }
            if (a.frequency === 'daily' && b.frequency !== 'daily') return -1;
            if (b.frequency === 'daily' && a.frequency !== 'daily') return 1;

            // Weekly quests next
            if (a.frequency === 'weekly' && b.frequency === 'weekly') {
                const today = new Date().getDay();
                const aDay = a.weekly_day ?? 1;
                const bDay = b.weekly_day ?? 1;
                let daysUntilA = aDay - today;
                let daysUntilB = bDay - today;
                if (daysUntilA < 0) daysUntilA += 7;
                if (daysUntilB < 0) daysUntilB += 7;
                return daysUntilA - daysUntilB;
            }
            if (a.frequency === 'weekly' && b.frequency !== 'daily' && b.frequency !== 'weekly') return -1;
            if (b.frequency === 'weekly' && a.frequency !== 'daily' && a.frequency !== 'weekly') return 1;

            // One-time quests last, sorted by points (highest first)
            return b.points - a.points;
        }) || [];
    }, [activeQuests]);

    // Group active quests by day
    const groupedQuests = React.useMemo(() => {
        if (!sortedActiveQuests || sortedActiveQuests.length === 0) {
            return [];
        }
        
        const groups: Array<{ key: string; label: string; quests: any[] }> = [];
        
        // Group quests by their day label
        const questMap = new Map<string, any[]>();
        
        sortedActiveQuests.forEach(quest => {
            const dayLabel = getQuestDayLabel(quest) || 'OTHER';
            if (!questMap.has(dayLabel)) {
                questMap.set(dayLabel, []);
            }
            questMap.get(dayLabel)!.push(quest);
        });

        // Order groups: TODAY, TOMORROW, SUNDAY, then others alphabetically
        const order = ['Today', 'Danes', 'Tomorrow', 'Jutri', 'SUNDAY', 'Sun'];
        
        order.forEach(label => {
            const group = Array.from(questMap.entries()).find(([key]) => key === label || key.includes(label));
            if (group) {
                groups.push({
                    key: label,
                    label: group[0],
                    quests: group[1]
                });
                questMap.delete(group[0]);
            }
        });
        
        // Add remaining groups
        Array.from(questMap.entries()).forEach(([key, quests]) => {
            groups.push({
                key: key,
                label: key,
                quests
            });
        });

        // Calculate total points for each group
        return groups.map(group => ({
            ...group,
            totalPoints: group.quests.reduce((sum, quest) => sum + (quest.points || 0), 0)
        }));
    }, [sortedActiveQuests]);

    // Group history by date (TODAY, YESTERDAY, then specific dates)
    const groupedHistory = React.useMemo(() => {
        if (!history || history.length === 0) {
            return [];
        }

        const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const today = startOfDay(new Date());
        const dayDiff = (a: Date, b: Date) => {
            const msPerDay = 24 * 60 * 60 * 1000;
            return Math.round((startOfDay(a).getTime() - startOfDay(b).getTime()) / msPerDay);
        };

        const dateKeyFor = (date: Date) => {
            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const d = String(date.getDate()).padStart(2, '0');
            return `${y}-${m}-${d}`;
        };

        const dateLabelFor = (date: Date) => {
            const diff = dayDiff(date, today);
            if (diff === 0) return t?.common?.today || 'Today';
            if (diff === -1) return t?.common?.yesterday || 'Yesterday';
            return date.toLocaleDateString();
        };

        const groups = new Map<string, { label: string; items: typeof history }>();
        for (const item of history) {
            const d = new Date(item.created_at);
            const key = dateKeyFor(d);
            const label = dateLabelFor(d);
            const existing = groups.get(key);
            if (existing) {
                existing.items.push(item);
            } else {
                groups.set(key, { label, items: [item] });
            }
        }

        // Sort items within each group by hour (most recent first)
        for (const group of groups.values()) {
            group.items.sort((a, b) => {
                const timeA = new Date(a.created_at).getTime();
                const timeB = new Date(b.created_at).getTime();
                return timeB - timeA; // Most recent first
            });
        }

        return Array.from(groups.entries())
            .sort((a, b) => b[0].localeCompare(a[0]))
            .map(([key, value]) => ({
                key,
                ...value,
                totalPoints: value.items.reduce((sum, item) => sum + (item.points || 0), 0),
            }));
    }, [history]);

    // Early return for loading state (after all hooks are defined)
    if (loadingBalance || loadingQuests || loadingCompletions || loadingHistory) {
        return <div className="p-8 text-center font-bold text-primary-600">{t?.common?.loading || 'Loading...'}</div>;
    }

    return (
            <div className="space-y-4 sm:space-y-6">
            {/* Hero Stats */}
            <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-2xl sm:rounded-3xl p-6 sm:p-10 text-white shadow-xl shadow-primary-200">
                <div className="flex flex-col items-center gap-[-70px] sm:gap-[-50px]">
                    <div className="-mt-8">
                      <NestQAvatar size={250} />
                    </div>
                    <div className="flex flex-col items-center text-center">
                        <h1 className="text-xl sm:text-2xl font-black mb-1">{getTimeBasedGreeting}!</h1>
                        <p className="opacity-80 font-medium text-sm">{t?.kidHome?.ready || "Ready for today's quests?"}</p>
                    </div>
                    <div className="bg-white/20 backdrop-blur-md rounded-2xl p-4 sm:p-5 flex items-center gap-3 sm:gap-4 mt-4 sm:mt-6">
                        <div className="bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl p-2 sm:p-3">
                            <Trophy className="text-white" size={28} />
                        </div>
                        <div>
                            <div className="text-xs font-bold uppercase tracking-widest opacity-80">{t?.kidHome?.totalPoints || 'Total Points'}</div>
                            <div className="text-2xl sm:text-3xl font-black">{balance || 0}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Quests Section */}
            <div className="space-y-3 sm:space-y-4">
                <div className="flex items-center gap-2">
                    <Star className="text-amber-400 fill-amber-400" size={20} />
                    <h2 className="text-lg sm:text-xl font-black text-slate-800 uppercase tracking-tight">{t?.kidHome?.activeQuests || 'Active Quests'}</h2>
                </div>
                
                {sortedActiveQuests?.length === 0 ? (
                    <div className="bg-white rounded-2xl p-6 sm:p-12 border-2 border-dashed border-primary-100 text-center">
                        <p className="text-slate-400 font-bold text-sm sm:text-base">{t?.kidHome?.noQuests || 'No quests assigned yet. Ask a parent for some!'}</p>
                    </div>
                ) : groupedQuests.length > 0 ? (
                    <div className="space-y-6">
                        {groupedQuests.map((group) => (
                            <div key={group.key} className="space-y-2 sm:space-y-3">
                                <div className="flex items-center justify-between px-1">
                                    <div className="flex items-center gap-2 flex-1">
                                        <div className="h-px bg-slate-200 flex-1"></div>
                                        <div className="text-xs sm:text-sm font-black text-slate-600 uppercase tracking-widest px-2 sm:px-3">
                                            {group.label}
                                        </div>
                                        <div className="h-px bg-slate-200 flex-1"></div>
                                    </div>
                                    <div className="text-xs sm:text-sm font-black text-amber-600 uppercase tracking-widest ml-2 sm:ml-4">
                                        {group.totalPoints} {t?.common?.pts || 'pts'}
                                    </div>
                                </div>
                                <div className="grid gap-3 sm:gap-4">
                                    {group.quests.map((quest) => (
                                        <div key={quest.id} className="group bg-white rounded-2xl p-4 sm:p-6 border-2 border-primary-50 hover:border-primary-400 transition-all shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                            <div className="flex items-center gap-3 sm:gap-4">
                                                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary-50 rounded-xl flex items-center justify-center text-primary-600 font-black text-sm sm:text-base">
                                                    {quest.points}
                                                </div>
                                                <div>
                                                    <h3 className="font-black text-base sm:text-lg text-slate-800">{quest.title}</h3>
                                                    <div className="flex flex-wrap gap-1 sm:gap-2">
                                                        <span className="text-xs font-bold text-primary-400 uppercase tracking-widest">
                                                            {getFrequencyLabel(quest.frequency)}
                                                        </span>
                                                        {getDailyTimeLabel(quest) && (
                                                            <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                                                                {getDailyTimeLabel(quest)}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => completeQuest.mutate(quest.id)}
                                                disabled={isQuestDisabled(quest)}
                                                className="flex items-center justify-center gap-2 bg-primary-50 text-primary-600 px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-black hover:bg-primary-600 hover:text-white transition-all transform active:scale-95 shadow-sm disabled:opacity-50 disabled:pointer-events-none text-sm sm:text-base min-w-[100px] sm:min-w-[120px]"
                                            >
                                                <Circle size={window.innerWidth < 640 ? 16 : 20} className="group-hover:hidden" />
                                                <CheckCircle size={window.innerWidth < 640 ? 16 : 20} className="hidden group-hover:block" />
                                                {t?.common?.done || 'Done!'}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    // Fallback to flat list if grouping fails
                    <div className="grid gap-3 sm:gap-4">
                        {sortedActiveQuests?.map((quest) => (
                            <div key={quest.id} className="group bg-white rounded-2xl p-4 sm:p-6 border-2 border-primary-50 hover:border-primary-400 transition-all shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div className="flex items-center gap-3 sm:gap-4">
                                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary-50 rounded-xl flex items-center justify-center text-primary-600 font-black text-sm sm:text-base">
                                        {quest.points}
                                    </div>
                                    <div>
                                        <h3 className="font-black text-base sm:text-lg text-slate-800">{quest.title}</h3>
                                        <div className="flex flex-wrap gap-1 sm:gap-2">
                                            <span className="text-xs font-bold text-primary-400 uppercase tracking-widest">
                                                {getFrequencyLabel(quest.frequency)}
                                            </span>
                                            {getQuestDayLabel(quest) && (
                                                <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                                                    {getQuestDayLabel(quest)}
                                                </span>
                                            )}
                                            {getDailyTimeLabel(quest) && (
                                                <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                                                    {getDailyTimeLabel(quest)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => completeQuest.mutate(quest.id)}
                                    disabled={isQuestDisabled(quest)}
                                    className="flex items-center justify-center gap-2 bg-primary-50 text-primary-600 px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-black hover:bg-primary-600 hover:text-white transition-all transform active:scale-95 shadow-sm disabled:opacity-50 disabled:pointer-events-none text-sm sm:text-base min-w-[100px] sm:min-w-[120px]"
                                >
                                    <Circle size={window.innerWidth < 640 ? 16 : 20} className="group-hover:hidden" />
                                    <CheckCircle size={window.innerWidth < 640 ? 16 : 20} className="hidden group-hover:block" />
                                    {t?.common?.done || 'Done!'}
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Completed Section */}
            {history && history.length > 0 && (
                <div className="space-y-3 sm:space-y-4">
                    <div className="flex items-center gap-2 text-slate-500">
                        <History size={window.innerWidth < 640 ? 16 : 20} />
                        <h2 className="text-lg sm:text-xl font-black uppercase tracking-tight">{t?.kidHome?.recentlyDone || 'Recently Done'}</h2>
                    </div>

                    <div className="space-y-4 sm:space-y-6">
                        {groupedHistory.map((group) => (
                            <div key={group.key} className="space-y-2 sm:space-y-3">
                                <div className="flex items-center justify-between px-1">
                                    <div className="flex items-center gap-2 flex-1">
                                        <div className="h-px bg-slate-200 flex-1"></div>
                                        <div className="text-xs sm:text-sm font-black text-slate-600 uppercase tracking-widest px-2 sm:px-3">
                                            {group.label}
                                        </div>
                                        <div className="h-px bg-slate-200 flex-1"></div>
                                    </div>
                                    <div className="text-xs sm:text-sm font-black text-amber-600 uppercase tracking-widest ml-2 sm:ml-4">
                                        {group.totalPoints} {t?.common?.pts || 'pts'}
                                    </div>
                                </div>
                                <div className="grid gap-2 sm:gap-3 opacity-80">
                                    {group.items.map((item) => (
                                        <div key={item.id} className="bg-slate-50 rounded-2xl p-3 sm:p-5 border border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-xl flex items-center justify-center text-slate-400 font-bold border border-slate-100 text-xs sm:text-sm">
                                                    {item.points || 0}
                                                </div>
                                                <div>
                                                    <h3 className="font-bold text-slate-600 text-sm sm:text-base">
                                                        {item.type === 'manual_points'
                                                            ? item.title
                                                            : item.quest?.title || item.title || 'Unknown Quest'}
                                                    </h3>
                                                    <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest">
                                                        {item.type === 'manual_points' ? (
                                                            <span className="text-primary-500 flex items-center gap-1">
                                                                <Gift size={window.innerWidth < 640 ? 10 : 12} /> Bonus
                                                            </span>
                                                        ) : item.status === 'pending' ? (
                                                            <span className="text-amber-500 flex items-center gap-1">
                                                                <Clock size={window.innerWidth < 640 ? 10 : 12} /> {t?.kidHome?.pendingApproval || 'Pending Approval'}
                                                            </span>
                                                        ) : item.status === 'approved' ? (
                                                            <span className="text-emerald-500 flex items-center gap-1">
                                                                <Check size={window.innerWidth < 640 ? 10 : 12} /> {t?.common?.approved || 'Approved'}
                                                            </span>
                                                        ) : (
                                                            <span className="text-rose-500">{t?.common?.rejected || 'Rejected'}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase">
                                                    {new Date(item.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </div>
                                                <div className="text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase">
                                                    {new Date(item.created_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Streak Placeholder */}
            <div className="bg-amber-50 rounded-2xl p-4 sm:p-6 border-2 border-amber-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3 sm:gap-4">
                    <span className="text-3xl sm:text-4xl">🔥</span>
                    <div>
                        <h3 className="font-black text-amber-900 leading-none text-lg sm:text-xl">3 {t?.kidHome?.streak || 'Day Streak!'}</h3>
                        <p className="text-amber-700 text-xs sm:text-sm font-bold opacity-70">{t?.kidHome?.keepItUp || 'Keep it up, hero!'}</p>
                    </div>
                </div>
                <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full ${i <= 3 ? 'bg-amber-500' : 'bg-amber-200'}`} />
                    ))}
                </div>
            </div>
        </div>
    );
};
